import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { format, isToday, isYesterday } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  MessageCircle, Send, Newspaper, Search, Zap, Trash2,
  ChevronDown, ChevronUp, Loader2, Users, ExternalLink, Sparkles,
} from 'lucide-react';
import { useTeamChatStore } from '../../../state/teamChatStore';
import { useAuthStore } from '../../../state/authStore';
import { getBackendBaseUrl } from '../../../lib/backendUrl';

const BACKEND = getBackendBaseUrl();

const TYPE_META = {
  shared_ai:       { icon: Sparkles, bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', label: 'AI Result' },
  shared_news:     { icon: Newspaper, bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', label: 'News' },
  shared_research: { icon: Search, bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Research' },
  shared_signal:   { icon: Zap, bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Signal' },
};

const AVATAR_COLORS = [
  'bg-emerald-600', 'bg-violet-600', 'bg-blue-600', 'bg-amber-600', 'bg-rose-600',
  'bg-cyan-600', 'bg-pink-600', 'bg-teal-600',
];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length > 1 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}

function fmtTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday ' + format(d, 'h:mm a');
  return format(d, 'MMM d, h:mm a');
}

function resolveViewUrl(payload) {
  if (!payload) return null;
  const t = payload.type;
  if (t === 'research' && payload.symbol) return `/public/india-company/${payload.symbol}`;
  if (t === 'news' && payload.url && !payload.url.startsWith('/public/news')) return payload.url;
  if (t === 'signal' && payload.symbol) return `/public/india-company/${payload.symbol}`;
  if (payload.url && payload.url !== '/public/ai' && payload.url !== '/public/news') return payload.url;
  return null;
}

const mdComponents = {
  p: ({ children }) => <p className="text-xs text-foreground/90 leading-relaxed mb-1.5">{children}</p>,
  h1: ({ children }) => <h3 className="text-sm font-bold text-foreground mt-2 mb-1">{children}</h3>,
  h2: ({ children }) => <h3 className="text-sm font-semibold text-foreground mt-2 mb-1">{children}</h3>,
  h3: ({ children }) => <h4 className="text-xs font-semibold text-foreground mt-1.5 mb-1">{children}</h4>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside text-xs text-foreground/90 mb-1.5 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-xs text-foreground/90 mb-1.5 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-xs text-foreground/90">{children}</li>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded border border-border">
      <table className="w-full text-[11px] text-foreground">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted text-muted-foreground">{children}</thead>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left font-medium border-b border-border">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1 border-b border-border/50">{children}</td>,
  code: ({ children }) => <code className="bg-muted text-emerald-500 px-1 py-0.5 rounded text-[10px]">{children}</code>,
  hr: () => <hr className="border-border my-2" />,
};

function SharedCard({ payload, type }) {
  const meta = TYPE_META[type];
  if (!payload || !meta) return null;
  const Icon = meta.icon;
  const [expanded, setExpanded] = useState(false);
  const viewUrl = resolveViewUrl(payload);

  const fullMd = payload.full_content || '';
  const summary = payload.summary || '';
  const expandableContent = fullMd || summary;
  const canExpand = expandableContent.length > 120;

  const previewText = summary
    .replace(/^\|.*\|$/gm, '').replace(/^[-|: ]+$/gm, '').replace(/[#*_~`>]/g, '')
    .replace(/\n{2,}/g, ' ').trim().slice(0, 150);

  return (
    <div className={`mt-1.5 rounded-lg border p-3 ${meta.bg} ${meta.border}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${meta.text}`} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
        {payload.symbol && (
          <Badge variant="outline" className="text-[9px] ml-1 px-1 py-0 border-border text-muted-foreground">
            {payload.symbol}
          </Badge>
        )}
      </div>

      {payload.title && <p className="text-sm font-semibold text-foreground leading-snug mb-1">{payload.title}</p>}

      {!expanded && previewText && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{previewText}{previewText.length >= 150 ? '...' : ''}</p>
      )}

      {expanded && (
        <div className="mt-1 max-h-[60vh] overflow-y-auto pr-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {expandableContent}
          </ReactMarkdown>
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        {canExpand && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`text-[11px] ${meta.text} hover:underline inline-flex items-center gap-0.5 font-medium`}
          >
            {expanded ? <>Collapse <ChevronUp className="w-3 h-3" /></> : <>Show full message <ChevronDown className="w-3 h-3" /></>}
          </button>
        )}

        {viewUrl && (
          <a href={viewUrl} className="text-[11px] text-emerald-400 hover:underline inline-flex items-center gap-1 font-medium">
            Open <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {payload.source && <p className="text-[10px] text-muted-foreground mt-1">{payload.source}</p>}
    </div>
  );
}

function DateSep({ date }) {
  const d = new Date(date);
  const label = isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'EEEE, MMMM d');
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function Bubble({ msg, isOwn, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const hasCard = msg.shared_payload && TYPE_META[msg.message_type];
  const showText = !hasCard || (msg.content && msg.content !== msg.shared_payload?.title);

  return (
    <div
      className={`group flex gap-3 ${isOwn ? 'flex-row-reverse' : ''} hover:bg-muted/50 px-4 py-1.5 rounded-lg transition-colors`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!isOwn && (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5 ${avatarColor(msg.sender_name)}`}>
          {initials(msg.sender_name)}
        </div>
      )}
      <div className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isOwn && (
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-semibold text-foreground">{msg.sender_name}</span>
            <span className="text-[10px] text-muted-foreground">{fmtTime(msg.created_at)}</span>
          </div>
        )}
        <div className={`rounded-xl px-3 py-2 text-sm ${
          isOwn
            ? 'bg-emerald-600/20 text-foreground rounded-tr-sm'
            : 'bg-muted text-foreground rounded-tl-sm'
        }`}>
          {showText && <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>}
          {hasCard && <SharedCard payload={msg.shared_payload} type={msg.message_type} />}
        </div>
        {isOwn && (
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{fmtTime(msg.created_at)}</span>
            {hovered && (
              <button onClick={() => onDelete(msg.id)} className="text-muted-foreground hover:text-red-500 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TeamChatPage() {
  const user = useAuthStore(s => s.user);
  const { messages, loading, sending, hasMore, error, fetchMessages, sendMessage, deleteMessage, startPolling, stopPolling } = useTeamChatStore();

  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState('');
  const [draft, setDraft] = useState('');
  const [members, setMembers] = useState([]);
  const bottomRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${BACKEND}/api/orgs`).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setOrgs(list);
      if (list.length && !activeOrgId) setActiveOrgId(list[0].id);
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!activeOrgId) return;
    fetchMessages(activeOrgId);
    startPolling(activeOrgId, 4000);
    axios.get(`${BACKEND}/api/orgs/${activeOrgId}`).then(({ data }) => {
      setMembers(Array.isArray(data?.members) ? data.members : []);
    }).catch(() => setMembers([]));
    return () => stopPolling();
  }, [activeOrgId, fetchMessages, startPolling, stopPolling]);

  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  const handleScroll = useCallback((e) => {
    const el = e?.target;
    if (!el) return;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeOrgId) return;
    const text = draft;
    setDraft('');
    await sendMessage(activeOrgId, text);
    setAutoScroll(true);
  };

  const activeOrg = orgs.find(o => o.id === activeOrgId);

  return (
    <div className="flex h-[calc(100vh-6rem)] gap-4 p-4 max-w-7xl mx-auto">
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3 px-1">
          <div className="p-2 rounded-lg bg-emerald-600/20">
            <MessageCircle className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-foreground">
              Team Chat {activeOrg ? `— ${activeOrg.name}` : ''}
            </h1>
            <p className="text-xs text-muted-foreground">
              {members.filter(m => m.status === 'active').length} active member{members.filter(m => m.status === 'active').length !== 1 ? 's' : ''}
            </p>
          </div>
          {orgs.length > 1 && (
            <select
              value={activeOrgId}
              onChange={e => setActiveOrgId(e.target.value)}
              className="bg-muted text-foreground text-sm rounded-lg border border-border px-3 py-1.5"
            >
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>

        {/* Messages */}
        <Card className="flex-1 border-border bg-card overflow-hidden flex flex-col relative">
          <div className="flex-1 overflow-y-auto px-2 py-3" onScroll={handleScroll}>
            {loading && !messages.length && (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {hasMore && (
              <button
                onClick={() => useTeamChatStore.getState().loadMore(activeOrgId)}
                className="block mx-auto mb-3 text-xs text-muted-foreground hover:text-foreground"
              >
                Load earlier messages
              </button>
            )}

            {!loading && !messages.length && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                <MessageCircle className="w-10 h-10 opacity-30" />
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            )}

            {messages.map((msg, i) => {
              const prev = messages[i - 1];
              const showDate = !prev || new Date(msg.created_at).toDateString() !== new Date(prev.created_at).toDateString();
              return (
                <React.Fragment key={msg.id}>
                  {showDate && <DateSep date={msg.created_at} />}
                  <Bubble
                    msg={msg}
                    isOwn={msg.sender_id === user?.id}
                    onDelete={(id) => activeOrgId && deleteMessage(activeOrgId, id)}
                  />
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {!autoScroll && messages.length > 5 && (
            <button
              onClick={() => { setAutoScroll(true); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              className="absolute bottom-20 right-6 bg-muted hover:bg-muted/80 text-foreground rounded-full p-2 shadow-lg z-10"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}

          <form onSubmit={handleSend} className="border-t border-border p-3 flex gap-2">
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Type a message..."
              className="bg-muted border-border text-foreground flex-1"
              autoFocus
            />
            <Button type="submit" disabled={sending || !draft.trim()} className="bg-emerald-600 hover:bg-emerald-700 px-4">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </Card>

        {error && <p className="text-xs text-red-400 mt-1 px-1">{error}</p>}
      </div>

      {/* Members sidebar */}
      <div className="hidden lg:block w-64 shrink-0">
        <Card className="border-border bg-card h-full">
          <div className="p-3 border-b border-border">
            <div className="flex items-center gap-2 text-foreground">
              <Users className="w-4 h-4" />
              <span className="text-sm font-semibold">Members</span>
              <Badge variant="secondary" className="ml-auto text-[10px]">{members.length}</Badge>
            </div>
          </div>
          <ScrollArea className="p-2" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
            {members.map(m => {
              const name = m.email?.split('@')[0] || 'Unknown';
              return (
                <div key={m.id || m.user_id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${avatarColor(name)}`}>
                    {initials(name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{name}</p>
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-muted-foreground">{m.role}</p>
                      {m.status === 'pending' && <Badge variant="outline" className="text-[8px] px-1 py-0 border-amber-600 text-amber-500">Pending</Badge>}
                    </div>
                  </div>
                </div>
              );
            })}
            {!members.length && <p className="text-xs text-muted-foreground p-2">No members found</p>}
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
